import React, { useCallback, useEffect, useState } from "react";

import { Toast } from "primereact/toast";
import { useRef } from "react";
import Dropdown, { DropdownOption } from "./Dropdown";
import { getStationsByCustomerId } from "../../services/clearing/customers";

interface CustomerOwnedStationDropdownProps {
  customerId: string;
  value: DropdownOption | null;
  onChange: (value: DropdownOption | null) => void;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
}

const CustomerOwnedStationDropdown: React.FC<
  CustomerOwnedStationDropdownProps
> = ({
  customerId,
  value,
  onChange,
  placeholder = "Select a station",
  className = "w-full",
  searchable = true,
}) => {
  const [ownedStations, setOwnedStations] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const fetchOwnedStationsByCustomerId = useCallback(
    async (customerId: string) => {
      if (!customerId) {
        setOwnedStations([]);
        return;
      }

      setLoading(true);

      try {
        const stations: string[] = await getStationsByCustomerId({
          customerId,
        });

        if (stations && stations?.length > 0) {
          const stationOptions: DropdownOption[] = stations.map((station) => ({
            name: station,
            code: station,
          }));
          setOwnedStations([...stationOptions, { name: "all", code: null }]);
        } else {
          setOwnedStations([]);
        }
      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch stations",
          life: 3000,
        });
        setOwnedStations([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchOwnedStationsByCustomerId(customerId);
  }, [customerId, fetchOwnedStationsByCustomerId]);

  return (
    <div>
      <Toast ref={toast} />
      <Dropdown
        options={ownedStations}
        value={value}
        onChange={onChange}
        placeholder={loading ? "Loading owned stations.." : placeholder}
        className={className}
        searchable={searchable}
      />
    </div>
  );
};

export default CustomerOwnedStationDropdown;
